/**
 * Pure helpers for `BoardDirectory.tsx`. Kept in a separate non-client
 * module so the My Boards filter logic can be exercised in unit tests
 * without standing up the full React tree the component otherwise drags
 * in (`useLobbyBoards`, `useLobbySeatHoldings`, `useWallet`, …).
 */

import type { MockBoard } from '../data/mock-boards';

/**
 * Build the "My Boards" base list — every live `MockBoard` the viewer
 * either:
 *
 *   1. Holds at least one (active or zombie) tile on, OR
 *   2. Is the original creator of, regardless of whether they hold any
 *      tiles on it.
 *
 * Rule (2) catches the "fresh creator with no other holdings" case the
 * old hold-only filter missed: `useLobbySeatHoldings` strips primary-
 * mint creator seats upstream (see the docstring on that hook) so a
 * brand-new creator's `heldIds` set is empty until they buy back one of
 * their own seats — without this branch their freshly-launched board
 * would be invisible in their own portfolio rail.
 *
 * `viewerLower` MUST already be lowercased; `MockBoard.creatorAddress`
 * is normalized lowercase upstream in `gridSummaryToMockBoard` so the
 * comparison is case-stable.
 */
export function selectMyBoards(
  allLive: MockBoard[],
  heldIds: Set<string>,
  viewerLower: string | null,
): MockBoard[] {
  return allLive.filter(
    (b) =>
      heldIds.has(b.id.toLowerCase()) ||
      (!!viewerLower && b.creatorAddress === viewerLower),
  );
}
